import { ReactNode } from 'react'
import L1InputLabel from '../l1/L1InputLabel'
import { V2L1FormPageBreak } from '../../uiv2/FormPage/V2L1FormPageBreak'
import L1Tip from '../l1/L1Tip'
import L1VSpace from '../l1/L1VSpace'
import { L2ColumnLayout, L2Column } from '../l2/L2ColumnLayout'
import L2ControlGrid from '../l2/L2ControlGrid'
import { L2ErfRowControls, L2ErfRowContent, L2ErfSpacerRow } from '../l2/L2ErfRow'
import L2Notice from '../l2/L2Notice'
import L2RootLayout from '../l2/L2RootLayout'
import { L4AmputationDiagrams } from '../l4/L4AmputationReview'
import L4BarcodeScanner, { L4LeaveBehindScanner } from '../l4/L4BarcodeScanner'
import L4Boolean from '../l4/L4Boolean'
import L4ConditionalDisplay from '../l4/L4ConditionalDisplay'
// import L4DatePicker from '../l4/L4DatePicker'
import L4DateSelect from '../l4/L4DateSelect'
import L4DefinedErrorCheckBlock from '../l4/L4DefinedErrorCheckBlock'
import { L4DiagnosisReview } from '../l4/L4DiagnosisReview'
import L4DocumentCamera from '../l4/L4DocumentCamera'
import L4DrugDisposalList from '../l4/L4DrugDisposalList'
import L4EncounterIds from '../l4/L4EncounterIds'
import L4Erf from '../l4/L4Erf'
import L4ErfRow from '../l4/L4ErfRow'
import L4FormSubmitButton from '../l4/L4FormSubmitButton'
import L4Hidden from '../l4/L4Hidden'
import L4JumpButton from '../l4/L4JumpButton'
import L4MahcAssessment from '../l4/L4MahcAssessment'
import L4Markdown from '../l4/L4Markdown'
import { L4MedicationHistoryTable } from '../l4/L4MedicationHistoryTable'
import { L4MedicationReconciliation } from '../l4/L4MedicationReconciliation'
import { L4NeurologicWeaknessHumanoid, L4NeurologicWeaknessReview } from '../l4/L4NeurologicWeaknessReview'
import { L4NoPrint, L4OnlyPrint, L4PrintBound, L4ShouldPrint } from '../l4/L4Print'
import L4OncologyReview from '../l4/L4OncologyReview'
import L4Page from '../l4/L4Page'
import L4Select from '../l4/L4Select'
import L4SelectOption from '../l4/L4SelectOption'
import L4Signature from '../l4/L4Signature'
import L4SignatureName from '../l4/L4SignatureName'
import L4Surface from '../l4/L4Surface'
import L4TextArea from '../l4/L4TextArea'
import L4TextField from '../l4/L4TextField'
import L4Title from '../l4/L4Title'
import L4TitleLabel from '../l4/L4TitleLabel'
import L4TitleNode from '../l4/L4TitleNode'
import L4ValueLabel from '../l4/L4ValueLabel'
import L4TabularList from '../l4/L4TabularList'
import L4UlcerReview from '../l4/L4UlcerReview'
import L4ValidationSummary from '../l4/L4ValidationSummary'
import L5Concept from './L5Concept'
import { V2LxLabsTable } from '../../uiv2/LabsTable/V2LxLabsTable'

const Proxy = ({children}: {children: ReactNode}) => <>{children}</>

// list all tags here that should be made available
// for direct use in the XFlow

const XFlowTags: {[name: string]: React.ElementType} = {
	'AmputationDiagrams': L4AmputationDiagrams,
	'BarcodeScanner': L4BarcodeScanner,
	'Blob': Proxy,
	'Boolean': L4Boolean,
	'Check': L4Boolean,
	'ColumnLayout': L2ColumnLayout,
	'Column': L2Column,
	'Concept': L5Concept,
	'ConditionalDisplay': L4ConditionalDisplay,
	'ControlGrid': L2ControlGrid,
	// [ISSUE-379] 20240910 disabling the new date picker until after form
	// versioning is released, we can fully validate the code changes and
	// stories and then switch it back here.  note also that there's a calc2
	// that shipped with the PR but didn't appear to be connected up yet.
	// full list of file changes here:
	// https://bitbucket.org/pndlm/fusion-advantage/pull-requests/31/diff
	// 'Date': L4DatePicker,
	'Date': L4TextField,
	'DateSelect': L4DateSelect,
	'DefinedErrorCheck': L4DefinedErrorCheckBlock,
	'DiagnosisReview': L4DiagnosisReview,
	'DocumentCamera': L4DocumentCamera,
	'DrugDisposalList': L4DrugDisposalList,
	'EncounterIds': L4EncounterIds,
	'Erf': L4Erf,
	'ErfRow': L4ErfRow,
	'ErfRowControls': L2ErfRowControls,
	'ErfRowContent': L2ErfRowContent,
	'ErfSpacerRow': L2ErfSpacerRow,
	'FormSubmitButton': L4FormSubmitButton,
	'Hidden': L4Hidden,
	'InputLabel': L1InputLabel,
	'JumpButton': L4JumpButton,
	'Label': ({title}) => <label>{title}</label>,
	'LabsTable': V2LxLabsTable,
	'LeaveBehindScanner': L4LeaveBehindScanner,
	'MahcAssessment': L4MahcAssessment,
	'Markdown': L4Markdown,
	'MedicationHistoryTable': L4MedicationHistoryTable,
	'MedicationReconciliation': L4MedicationReconciliation,
	'NeurologicWeaknessReview': L4NeurologicWeaknessReview,
	'NeurologicWeaknessDiagrams': L4NeurologicWeaknessHumanoid,
	'NoPrint': L4NoPrint,
	'Notice': L2Notice,
	'Nothing': () => <div />, // usually to fill an unused grid or control space
	'OncologyReview': L4OncologyReview,
	'OnlyPrint': L4OnlyPrint,
	'Option': L4SelectOption,
	'Page': L4Page,
	'PageBreak': V2L1FormPageBreak,
	'PrintBound': L4PrintBound, 
	'Proxy': Proxy,
	'RootLayout': L2RootLayout,
	'Select': L4Select,
	'ShouldPrint': L4ShouldPrint,
	'Surface': L4Surface,
	'TabularList': L4TabularList,
	'TextField': L4TextField,
	'TextArea': L4TextArea,
	'Tip': L1Tip,
	'Title': L4Title,
	'TitleLabel': L4TitleLabel,
	'TitleNode': L4TitleNode,
	'Signature': L4Signature,
	'SignatureName': L4SignatureName,
	'UlcerReview': L4UlcerReview,
	'ValidationSummary': L4ValidationSummary,
	'ValueLabel': L4ValueLabel,
	'ValueListBooleans': Proxy,
	'ValueListOptions': Proxy,
	'VSpace': L1VSpace,
	'XFlow': Proxy,  // root XML element
}

export default XFlowTags
